// DeepLinkHandler — VIN-2026-86
//
// Routes an opened iOS Universal Link into the in-app React Router so an
// event-invite email link (https://www.vinarai.com/event/{id}/join?…) opens
// the live app on the right page instead of bouncing to Safari.
//
// Two entry points, both covered:
//   • Cold start — the app was launched BY the link. The listener may attach
//     after the OS already fired the event, so we replay App.getLaunchUrl()
//     once on mount.
//   • Warm open — link tapped while the app is backgrounded/foregrounded →
//     the appUrlOpen listener fires.
//
// We only act on our OWN domain links (vinarai.com) and navigate to the
// path+query, so the existing route + auth gate take over (JoinEvent handles
// sign-in for guests). iOS-only; on web this is a no-op (links already load
// the SPA directly). Uses the global Capacitor plugin proxy — no dynamic
// import chunk to preload (same rationale as initNativeKeyboard).

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

function isNativePlatform() {
  return typeof window !== 'undefined'
    && typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()
}

export default function DeepLinkHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!isNativePlatform()) return
    const AppPlugin = window.Capacitor?.Plugins?.App
    if (!AppPlugin) return

    const routeFromUrl = (url) => {
      if (!url) return
      try {
        const u = new URL(url)
        // Only our universal links — ignore the capacitor://localhost launch
        // URL and any third-party scheme.
        if (!/(^|\.)vinarai\.com$/i.test(u.hostname)) return
        const target = (u.pathname || '/') + (u.search || '') + (u.hash || '')
        if (target && target !== '/') navigate(target)
      } catch { /* malformed URL — ignore */ }
    }

    // Cold-start: replay the URL the app was launched with (once).
    AppPlugin.getLaunchUrl?.().then(res => routeFromUrl(res?.url)).catch(() => {})

    // Warm opens.
    let handle
    Promise.resolve(AppPlugin.addListener?.('appUrlOpen', (data) => routeFromUrl(data?.url)))
      .then(h => { handle = h })
      .catch(() => {})

    return () => { try { handle?.remove?.() } catch { /* noop */ } }
  }, [navigate])

  return null
}
